import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  LinearProgress,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
  SvgIcon,
} from "@mui/material";

import {
  CheckCircle,
  Check,
  NavigateBefore,
  NavigateNext,
  CalendarMonth,
  EmojiEvents,
  Star,
  Home,
  CardGiftcard,
  Lock,
  InfoOutlined,
} from "@mui/icons-material";
import {
  StyledMainContainer,
  StyledRightPanel,
  StyledRetoCard,
} from "./styled";

import { colors } from "../../theme/colors";

import AddIcon from "@mui/icons-material/Add";

import { ReactComponent as MedallaLogo } from "../../img/svg/medalla-retos.svg";

const monthNames = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export default function RecompensasMensuales() {
  const navigate = useNavigate();
  const { monthNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [challengeData, setChallengeData] = useState(null);
  const [prizesData, setPrizesData] = useState(null);
  const [currentYear] = useState(2025);

  const fetchChallengeData = async () => {
    setLoading(true);
    setError(null);

    try {
      const challengeResponse = await fetch(
        `${process.env.REACT_APP_BE_URL}/rtm/challenges/${monthNumber}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      if (challengeResponse.status === 403) {
        throw new Error("No tienes permisos para acceder a esta información");
      }

      const challengeData = await challengeResponse.json();

      if (challengeData.error) {
        throw new Error(
          challengeData.errorMessage || "Error en la respuesta del servidor",
        );
      }

      setChallengeData(challengeData);

      const prizesResponse = await fetch(
        `${process.env.REACT_APP_BE_URL}/rtm/prizes?m=${monthNumber}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      if (prizesResponse.status === 403) {
        throw new Error("No tienes permisos para acceder a los premios");
      }

      const prizesData = await prizesResponse.json();

      if (prizesData.error) {
        throw new Error(
          prizesData.errorMessage ||
            "Error en la respuesta del servidor de premios",
        );
      }

      setPrizesData(prizesData);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (monthNumber) {
      fetchChallengeData();
    }
  }, [monthNumber]);

  const getRewardStatus = (estatus, porcentajeAvance) => {
    if (estatus === 1) return "claimed";
    if (porcentajeAvance === 1) return "completed";
    if (porcentajeAvance > 0) return "in_progress";
    return "not_started";
  };

  const mapPrizesToRewards = (prizesData) => {
    if (!prizesData?.result) return [];

    const allPrizes = [
      ...(prizesData.result.completed || []),
      ...(prizesData.result.on_course?.claimed || []),
      ...(prizesData.result.on_course?.not_claimed || []),
    ];

    return allPrizes.map((prize) => ({
      id: prize.PremioId,
      title: prize.NombreRecompensa,
      description: prize.DescripcionRecompensa,
      progress: Math.round(prize.PorcentajeAvance * 100),
      total: 100,
      status: getRewardStatus(prize.Estatus, prize.PorcentajeAvance),
      deadline: prize.FechaLimite,
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Mostrar loading
  if (loading) {
    return (
      <StyledMainContainer style={{ justifyContent: "center" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            width: "100%",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <CircularProgress sx={{ color: "#4fc3d7" }} />
          <Typography sx={{ color: "white" }}>
            Cargando recompensas...
          </Typography>
        </div>
      </StyledMainContainer>
    );
  }

  // Mostrar error
  if (error) {
    return (
      <StyledMainContainer style={{ justifyContent: "center" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            width: "100%",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <Typography sx={{ color: "white", textAlign: "center" }}>
            Error al cargar las recompensas: {error}
          </Typography>
          <button
            onClick={fetchChallengeData}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#4fc3d7",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Reintentar
          </button>
        </div>
      </StyledMainContainer>
    );
  }

  const recompensas = mapPrizesToRewards(prizesData);

  return (
    <StyledMainContainer white style={{ flexDirection: "column", gap: "2rem" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          color: "white",
          mb: 2,
          mt: 2,
        }}
      >
        <IconButton
          sx={{ color: colors.primary }}
          onClick={() => navigate("/")}
        >
          <NavigateBefore />
        </IconButton>
        <CalendarMonth sx={{ fontSize: "2rem", color: colors.primary }} />
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: colors.primary, fontSize: "1.5rem" }}
        >
          {monthNames[parseInt(monthNumber) - 1]} {currentYear}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row", md: "row", lg: "row" },
          gap: "3rem",
          width: "100%",
          padding: { xs: "0 36px", md: "0 36px", lg: "1.25rem" },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h5"
            sx={{
              color: colors.secondary,
              fontWeight: "700",
              mb: 1,
              fontSize: "22px",
            }}
          >
            Recompensas de este mes
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                color: colors.gray.textLight,
                mb: 4,
                fontSize: "1rem",
              }}
            >
              Para obtener las recompensas debes completar los retos.
            </Typography>

            <Tooltip
              title="Ve a la parte de Recompensas para obtener tus premios."
              placement="top"
            >
              <InfoOutlined
                sx={{
                  color: colors.primary,
                  fontSize: "1.2rem",
                  cursor: "pointer",
                  mb: 4,
                }}
              />
            </Tooltip>
          </Box>

          <Box
            sx={{
              display: { xs: "flex", md: "block" },
              overflowX: { xs: "auto", md: "visible" },
              gap: { xs: 2, md: 0 },
              padding: { xs: "1rem", md: 0 },
              "&::-webkit-scrollbar": {
                height: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#a1a1a1",
              },
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                display: { xs: "none", md: "flex" },
                flexDirection: "row",
              }}
            >
              {recompensas.map((recompensa) => (
                <Grid item xs={12} md={6} key={recompensa.id}>
                  <StyledRetoCard
                    completed={recompensa.status === "completed"}
                    onClick={() => {}}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="reto-content">
                      {recompensa.status === "completed" && (
                        <CheckCircle sx={{ color: colors.green.secondary }} />
                      )}
                      {recompensa.status === "in_progress" && (
                        <EmojiEvents sx={{ color: "#ffd15c" }} />
                      )}
                      {recompensa.status === "claimed" && (
                        <Star sx={{ color: colors.primary }} />
                      )}
                      {recompensa.status === "not_started" && (
                        <Lock sx={{ color: "#999" }} />
                      )}

                      <div className="reto-text">
                        <Typography className="reto-title">
                          {recompensa.title}
                        </Typography>

                        {recompensa.status !== "claimed" && (
                          <LinearProgress
                            variant="determinate"
                            value={recompensa.progress}
                            sx={{
                              height: "6px",
                              margin: "0px !important",
                              borderRadius: "3px",
                              backgroundColor: "#E8EFF7",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor:
                                  recompensa.status === "completed"
                                    ? colors.green.secondary
                                    : recompensa.status === "in_progress"
                                      ? colors.primary
                                      : colors.primary,
                              },
                              marginBottom: "0.5rem",
                            }}
                          />
                        )}

                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "#666",
                            mt: 0.5,
                          }}
                        >
                          {recompensa.status === "claimed"
                            ? "¡Recompensa reclamada!"
                            : recompensa.description}
                        </Typography>
                      </div>
                    </div>
                  </StyledRetoCard>
                </Grid>
              ))}
            </Grid>

            {/* En móvil: Cards en fila con scroll horizontal */}
            {recompensas.map((recompensa) => (
              <Box
                key={recompensa.id}
                sx={{
                  display: { xs: "block", md: "none" },
                  minWidth: "280px",
                  flexShrink: 0,
                }}
              >
                <StyledRetoCard
                  completed={recompensa.status === "completed"}
                  onClick={() => {}}
                  style={{ cursor: "pointer", height: "100%" }}
                >
                  <div className="reto-content">
                    {recompensa.status === "completed" && (
                      <CheckCircle sx={{ color: colors.green.secondary }} />
                    )}
                    {recompensa.status === "in_progress" && (
                      <EmojiEvents sx={{ color: "#ffd15c" }} />
                    )}
                    {recompensa.status === "claimed" && (
                      <Star sx={{ color: colors.primary }} />
                    )}
                    {recompensa.status === "not_started" && (
                      <Lock sx={{ color: "#999" }} />
                    )}

                    <div className="reto-text">
                      <Typography className="reto-title">
                        {recompensa.title}
                      </Typography>

                      {recompensa.status !== "claimed" && (
                        <LinearProgress
                          variant="determinate"
                          value={recompensa.progress}
                          sx={{
                            height: "6px",
                            margin: "0px !important",
                            borderRadius: "3px",
                            backgroundColor: "#E8EFF7",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor:
                                recompensa.status === "completed"
                                  ? colors.green.secondary
                                  : recompensa.status === "in_progress"
                                    ? colors.primary
                                    : colors.primary,
                            },
                            marginBottom: "0.5rem",
                          }}
                        />
                      )}

                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: "#666",
                          mt: 0.5,
                        }}
                      >
                        {recompensa.status === "claimed"
                          ? "¡Recompensa reclamada!"
                          : recompensa.description}
                      </Typography>
                    </div>
                  </div>
                </StyledRetoCard>
              </Box>
            ))}
          </Box>

          {recompensas.length === 0 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <SvgIcon
                component={MedallaLogo}
                inheritViewBox
                sx={{ fontSize: "2rem", color: colors.primary }}
              />
              <Typography sx={{ color: "#999", mt: 2 }}>
                No hay recompensas disponibles para este mes
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              mt: 6,
              maxWidth: "450px",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: colors.secondary,
                fontWeight: "700",
                mb: 1,
                fontSize: "24px",
              }}
            >
              Metas cumplidas
            </Typography>

            <Typography
              sx={{
                color: colors.gray.textLight,
                fontSize: "14px",
                mb: 3,
              }}
            >
              {monthNames[parseInt(monthNumber) - 1]}{" "}
              {monthNumber <= 5
                ? new Date().getFullYear() - 1
                : new Date().getFullYear()}
            </Typography>

            {challengeData?.result?.response?.completed?.length > 0 ? (
              <Box
                sx={{
                  position: "relative",
                  maxHeight: { xs: "400px", md: "400px" },
                  overflow: "auto",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: "15px",
                      top: "0px",
                      bottom: "0px",
                      width: "2px",
                      backgroundColor: colors.gray.textLight,
                      zIndex: 0,
                    },
                  }}
                >
                  {challengeData.result.response.completed.map(
                    (meta, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: colors.green.secondary,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Check
                            sx={{
                              color: "white",
                              fontSize: "24px",
                            }}
                          />
                        </Box>

                        <Box sx={{ flex: 1, pt: 0.5 }}>
                          <Typography
                            sx={{
                              color: "#003865",
                              fontWeight: "600",
                              fontSize: "16px",
                              mb: 0.5,
                              lineHeight: 1.3,
                            }}
                          >
                            {meta.name}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#999",
                              fontSize: "14px",
                            }}
                          >
                            Reto cumplido
                          </Typography>
                        </Box>
                      </Box>
                    ),
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <SvgIcon
                  component={MedallaLogo}
                  inheritViewBox
                  sx={{ fontSize: "2rem", color: colors.primary }}
                />
                <Typography sx={{ color: "#999", mt: 2 }}>
                  Aún no tienes metas cumplidas
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ width: { xs: "auto", md: "400px" } }}>
          <Typography
            variant="h5"
            sx={{
              color: colors.secondary,
              fontWeight: "700",
              fontSize: "24px",
              mb: 3,
            }}
          >
            Pendientes
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxWidth: "100%",
              maxHeight: { xs: "400px", md: "100%" },
              overflowY: { xs: "auto", md: "visible" },
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#a1a1a1",
              },
            }}
          >
            {challengeData?.result?.response?.onCourse?.length > 0 ? (
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: "15px",
                    top: "0px",
                    bottom: "0px",
                    width: "2px",
                    backgroundColor: colors.gray.textLight,
                    zIndex: 0,
                  }}
                />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {challengeData.result.response.onCourse.map((meta, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          backgroundColor: colors.secondary,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <AddIcon
                          sx={{
                            color: "white",
                            fontSize: "18px",
                          }}
                        />
                      </Box>

                      <Box sx={{ flex: 1, pt: 0.5 }}>
                        <Typography
                          sx={{
                            color: "#003865",
                            fontWeight: "600",
                            fontSize: "16px",
                            mb: 0.5,
                            lineHeight: 1.3,
                          }}
                        >
                          {meta.name}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#999",
                            fontSize: "14px",
                          }}
                        >
                          Tienes hasta el:{" "}
                          <strong>{formatDate(meta.date)}</strong>
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <SvgIcon
                  component={MedallaLogo}
                  inheritViewBox
                  sx={{ fontSize: "2rem", color: colors.primary }}
                />
                <Typography sx={{ color: "#999", mt: 2 }}>
                  No tienes ningún reto pendiente
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </StyledMainContainer>
  );
}
